import { useEffect, useMemo, useState } from 'react';
import { Outlet, useLocation, useParams } from 'react-router-dom';
import { useImmer } from 'use-immer';

import { AdminSection, ResponseError } from '@typings';
import { toast } from '@features';
import { getCluster, getOrganizations } from '@services';
import { userSelector } from '@selectors';
import { useFetchList, useSelector } from '@hooks';
import { path } from '@utils';

import { BillingTabs } from '@components/Billing';
import { ChaseSpinner, Render } from '@components/Shared';

export const AdminSectionClusterDetailsLayout = () => {
  const { clusterName } = useParams();
  const { pathname } = useLocation();

  const { systemRole } = useSelector(userSelector);

  const [isLoading, setLoading] = useState(Boolean(clusterName));
  const [cluster, setCluster] = useImmer<AdminSection.Cluster | null>(null);

  const {
    isFetched,
    list: allClusterOrganizations,
    isLoading: isLoadingClusterOrganizations,
    getList,
  } = useFetchList({
    getList: getOrganizations,
    fetchOnMount: false,
  });

  useEffect(() => {
    if (
      clusterName &&
      !isFetched &&
      systemRole !== AdminSection.Role.User &&
      pathname ===
        path.clusterDetails(
          clusterName,
          AdminSection.ClusterDetailsTab.Organizations,
        )
    ) {
      getList();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname]);

  useEffect(() => {
    (async () => {
      try {
        if (!clusterName) {
          return;
        }

        setLoading(true);

        const clusterData = await getCluster({ clusterName });
        setCluster(clusterData);
      } catch (error) {
        const errorMessage = (error as ResponseError)?.error;

        toast.error(errorMessage ?? 'Failed to load cluster');
      } finally {
        setLoading(false);
      }
    })();
  }, [clusterName, setCluster]);

  const [rightTabComponent, setRightTabComponent] =
    useState<React.ReactNode>(null);

  const tabs = useMemo(
    () =>
      clusterName
        ? [
            {
              title: 'Nodes',
              path: path.clusterDetails(
                clusterName,
                AdminSection.ClusterDetailsTab.Nodes,
              ),
            },
            {
              title: 'Reports',
              path: path.clusterDetails(
                clusterName,
                AdminSection.ClusterDetailsTab.Reports,
              ),
            },
            {
              title: 'Resources',
              path: path.clusterDetails(
                clusterName,
                AdminSection.ClusterDetailsTab.Resources,
              ),
            },
            {
              title: 'Organizations',
              path: path.clusterDetails(
                clusterName,
                AdminSection.ClusterDetailsTab.Organizations,
              ),
            },
          ]
        : [],
    [clusterName],
  );

  if (isLoading) {
    return (
      <div className="relative flex min-h-[400px] w-full items-center justify-center">
        <ChaseSpinner color="black" className="h-12 w-12" />
      </div>
    );
  }

  if (!cluster) {
    return (
      <div className="flex flex-1 items-center justify-center py-10 text-h5 text-neural-04">
        No cluster found
      </div>
    );
  }

  return (
    <div className="mt-8 flex-1 rounded-2xl bg-white pb-14">
      <BillingTabs tabs={tabs} rightTabComponent={rightTabComponent} />

      <Render if={isLoadingClusterOrganizations}>
        <div className="relative flex min-h-[400px] w-full items-center justify-center">
          <ChaseSpinner color="black" className="h-12 w-12" />
        </div>
      </Render>

      <Render if={!isLoadingClusterOrganizations}>
        <Outlet
          context={{
            cluster,
            allClusterOrganizations,
            setCluster,
            setRightTabComponent,
          }}
        />
      </Render>
    </div>
  );
};
